import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WhitelistPanel.css';

// ═══════════════════════════════════════════════════════════════════════════
// WHITELIST PANEL
// Displays whitelisted objects with full enriched metadata (name, icon, price).
// Removal is routed through App.jsx handleUnwhitelist so session refs and
// Supabase stay in sync. Manual entry still supported.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatUsd(value) {
  if (!value || value <= 0) return null;
  if (value < 0.01) return '<$0.01';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortId(id) {
  if (!id || id.length <= 16) return id;
  return `${id.slice(0, 8)}...${id.slice(-8)}`;
}

/**
 * Build a display map keyed by objectId.
 * Priority order for metadata:
 *   1. localStorage entry metadata (stored at whitelist time — survives refresh)
 *   2. recentlyWhitelisted ref (in-session cache, pre-refresh)
 *   3. userObjects / protectedAssets (live wallet data)
 * This means the panel always displays correctly after page refresh.
 */
function buildEnrichmentMap(entries, userObjects, protectedAssets, recentlyWhitelisted = {}) {
  const byId = {};

  // Pass 1: seed from live wallet data (lowest priority — overwritten by stored meta)
  const allLiveObjects = [
    ...(userObjects || []),
    ...(protectedAssets || []),
    ...Object.values(recentlyWhitelisted).filter(o => o?.data?.objectId),
  ];
  allLiveObjects.forEach(obj => {
    const id = obj?.data?.objectId;
    if (!id || byId[id]) return;
    const enriched = obj.enriched || {};
    byId[id] = {
      name: enriched.displayName || enriched.symbol || obj.data?.type?.split('::').pop() || null,
      symbol: enriched.symbol || null,
      iconUrl: enriched.iconUrl || null,
      balance: enriched.formattedBalance || null,
      rawBalance: enriched.rawBalance || 0,
      decimals: enriched.metadata?.decimals ?? 9,
      coinType: enriched.coinType || null,
      usdValue: obj.usdValue || 0,
    };
  });

  // Pass 2: overwrite with stored metadata from localStorage entries (highest priority)
  // This data was captured at whitelist time and survives page refresh
  entries.forEach(entry => {
    if (!entry.id) return;
    const hasStoredMeta = entry.name || entry.symbol || entry.coinType;
    if (hasStoredMeta) {
      byId[entry.id] = {
        name: entry.name || byId[entry.id]?.name || null,
        symbol: entry.symbol || byId[entry.id]?.symbol || null,
        iconUrl: entry.iconUrl || byId[entry.id]?.iconUrl || null,
        balance: byId[entry.id]?.balance || null,
        rawBalance: entry.rawBalance || byId[entry.id]?.rawBalance || 0,
        decimals: entry.decimals ?? byId[entry.id]?.decimals ?? 9,
        coinType: entry.coinType || byId[entry.id]?.coinType || null,
        usdValue: byId[entry.id]?.usdValue || 0,
      };
    }
  });

  return byId;
}

/**
 * Group whitelist entries by coinType for display.
 * Each entry.id is an objectId. We aggregate same-coinType entries into one row.
 * Returns array of display items: { displayKey, ids[], name, symbol, iconUrl, balance, usdValue, isGroup }
 */
function groupEntriesForDisplay(entries, enrichmentMap) {
  const coinTypeGroups = {};
  const soloEntries = [];

  entries.forEach(entry => {
    const info = enrichmentMap[entry.id];
    const ct = info?.coinType;

    if (ct) {
      if (!coinTypeGroups[ct]) {
        coinTypeGroups[ct] = {
          displayKey: ct,
          ids: [],
          name: info.name,
          symbol: info.symbol,
          iconUrl: info.iconUrl,
          totalRawBalance: 0,
          decimals: info.decimals,
          usdValue: 0,
          isGroup: false,
        };
      }
      coinTypeGroups[ct].ids.push(entry.id);
      coinTypeGroups[ct].totalRawBalance += parseFloat(info.rawBalance || 0);
      coinTypeGroups[ct].usdValue += info.usdValue || 0;
      if (!coinTypeGroups[ct].iconUrl && info.iconUrl) coinTypeGroups[ct].iconUrl = info.iconUrl;
    } else {
      // No coinType — solo entry (NFT, non-coin object, or unknown)
      soloEntries.push({
        displayKey: entry.id,
        ids: [entry.id],
        name: info?.name || 'Unknown Object',
        symbol: info?.symbol || null,
        iconUrl: info?.iconUrl || null,
        balance: info?.balance || null,
        usdValue: info?.usdValue || 0,
        isGroup: false,
        isUnknown: !info,
        entryId: entry.id,
      });
    }
  });

  const groupedRows = Object.values(coinTypeGroups).map(g => {
    const formattedBalance = g.totalRawBalance > 0
      ? (g.totalRawBalance / Math.pow(10, g.decimals)).toLocaleString('en-US', {
          minimumFractionDigits: 2, maximumFractionDigits: 6,
        })
      : null;
    return {
      ...g,
      isGroup: g.ids.length > 1,
      balance: formattedBalance ? `${formattedBalance} ${g.symbol || ''}`.trim() : null,
    };
  });

  return [...groupedRows, ...soloEntries];
}

// ─── Main Component ────────────────────────────────────────────────────────

const WhitelistPanel = ({ isOpen, onClose, isAdmin = false, userObjects = [], protectedAssets = [], onUnwhitelist, recentlyWhitelisted = {} }) => {
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [newAddress, setNewAddress] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [removingId, setRemovingId] = useState(null); // track in-flight removal

  // Build enrichment map — localStorage metadata takes priority over live wallet data
  // This means display is correct immediately after page refresh
  const enrichmentMap = useMemo(
    () => buildEnrichmentMap(whitelistEntries, userObjects, protectedAssets, recentlyWhitelisted),
    [whitelistEntries, userObjects, protectedAssets, recentlyWhitelisted]
  );

  useEffect(() => {
    if (isOpen) loadWhitelist();
  }, [isOpen]);

  // Re-sync when userObjects/protectedAssets change (e.g. after removal updates state)
  useEffect(() => {
    if (isOpen) loadWhitelist();
  }, [userObjects, protectedAssets]);

  const loadWhitelist = () => {
    try {
      const saved = localStorage.getItem('objectWhitelist');
      if (saved) {
        const whitelist = JSON.parse(saved);
        const entries = Object.entries(whitelist).map(([id, data]) => ({ id, ...data }));
        setWhitelistEntries(entries);
      } else {
        setWhitelistEntries([]);
      }
    } catch (err) {
      console.error('Error loading whitelist:', err);
      setWhitelistEntries([]);
    }
  };

  const validateAddress = (address) => {
    const suiAddressRegex = /^0x[a-fA-F0-9]{64}$/;
    const objectIdRegex = /^(0x)?[a-fA-F0-9_:]+$/;
    return suiAddressRegex.test(address) || objectIdRegex.test(address);
  };

  const handleAddAddress = () => {
    setError('');
    setSuccessMessage('');
    if (!newAddress.trim()) { setError('Please enter an object ID'); return; }
    if (!validateAddress(newAddress.trim())) { setError('Invalid format — please enter a valid object ID'); return; }

    try {
      const saved = localStorage.getItem('objectWhitelist') || '{}';
      const whitelist = JSON.parse(saved);
      if (whitelist[newAddress.trim()]) { setError('Object ID already whitelisted'); return; }

      whitelist[newAddress.trim()] = {
        timestamp: Date.now(),
        addedBy: 'admin',
        reason: 'Manually added via Whitelist Panel',
      };
      localStorage.setItem('objectWhitelist', JSON.stringify(whitelist));
      setSuccessMessage('✓ Object added to whitelist');
      setNewAddress('');
      loadWhitelist();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding to whitelist:', err);
      setError('Failed to add object');
    }
  };

  const handleRemove = async (ids) => {
    // ids is always an array of objectIds
    const firstId = ids[0];
    setRemovingId(firstId);
    try {
      for (const id of ids) {
        if (onUnwhitelist) {
          await onUnwhitelist(id, false); // always false — all entries are objectIds now
        } else {
          // Fallback: direct localStorage (no wallet connected)
          const saved = localStorage.getItem('objectWhitelist') || '{}';
          const whitelist = JSON.parse(saved);
          delete whitelist[id];
          localStorage.setItem('objectWhitelist', JSON.stringify(whitelist));
        }
      }
      loadWhitelist();
      setSuccessMessage('✓ Removed from whitelist');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error removing from whitelist:', err);
      setError('Failed to remove — try again');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearAll = async () => {
    localStorage.setItem('objectWhitelist', '{}');
    if (onUnwhitelist) {
      for (const entry of whitelistEntries) {
        try { await onUnwhitelist(entry.id, false); } catch (e) { /* continue */ }
      }
    }
    setSuccessMessage('✓ Whitelist cleared');
    setConfirmClear(false);
    loadWhitelist();
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="whitelist-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="whitelist-panel"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="whitelist-header">
            <div className="whitelist-title-section">
              <h2>🛡️ Whitelist Manager</h2>
              <p className="whitelist-subtitle">
                {isAdmin ? 'Manage Protected Objects' : 'Your Protected Objects'}
              </p>
            </div>
            <button className="close-button" onClick={onClose}>✕</button>
          </div>

          {/* Manual Add (Admin Only) */}
          {isAdmin && (
            <div className="add-address-section compact">
              <h3>Add Object to Whitelist</h3>
              <div className="add-address-form">
                <input
                  type="text"
                  value={newAddress}
                  onChange={e => setNewAddress(e.target.value)}
                  placeholder="Object ID or coin type (e.g. 0x123...)"
                  className="address-input"
                  onKeyPress={e => { if (e.key === 'Enter') handleAddAddress(); }}
                />
                <button className="add-button" onClick={handleAddAddress}>
                  Add
                </button>
              </div>
              <AnimatePresence>
                {error && (
                  <motion.div className="message error-message" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    ⚠️ {error}
                  </motion.div>
                )}
                {successMessage && (
                  <motion.div className="message success-message" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    {successMessage}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Non-admin success/error messages */}
          {!isAdmin && (
            <AnimatePresence>
              {(error || successMessage) && (
                <motion.div
                  className={`message ${error ? 'error-message' : 'success-message'}`}
                  style={{ margin: '0 16px 8px' }}
                  initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                >
                  {error || successMessage}
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* Entries */}
          <div className="whitelist-entries-section">
            <div className="entries-header">
              <h3>Protected Objects ({whitelistEntries.length})</h3>
              {whitelistEntries.length > 0 && (
                confirmClear ? (
                  <div className="confirm-clear-inline">
                    <span>Sure?</span>
                    <button className="confirm-yes-button" onClick={handleClearAll}>Yes, clear</button>
                    <button className="confirm-no-button" onClick={() => setConfirmClear(false)}>Cancel</button>
                  </div>
                ) : (
                  <button className="clear-all-button" onClick={() => setConfirmClear(true)}>Clear All</button>
                )
              )}
            </div>

            <div className="whitelist-list">
              {whitelistEntries.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🛡️</div>
                  <p>No whitelisted objects</p>
                  <p className="empty-subtitle">
                    {isAdmin
                      ? 'Add object IDs above, or use the (⋮) menu on any item'
                      : 'Use the (⋮) menu on any item in the cleanup list to whitelist it'}
                  </p>
                </div>
              ) : (
                groupEntriesForDisplay(whitelistEntries, enrichmentMap).map(row => {
                  const isRemoving = removingId === row.ids[0];
                  return (
                    <WhitelistEntry
                      key={row.displayKey}
                      row={row}
                      isRemoving={isRemoving}
                      onRemove={handleRemove}
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* Info */}
          <div className="whitelist-info compact">
            <h4>ℹ️ How Whitelist Works</h4>
            <ul>
              <li>Whitelisted objects are hidden from the cleanup list</li>
              <li>Use (⋮) menu on any item to whitelist it</li>
              <li>Syncs across devices automatically</li>
            </ul>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Whitelist Entry Row ───────────────────────────────────────────────────

function WhitelistEntry({ row, isRemoving, onRemove }) {
  const usd = formatUsd(row.usdValue);
  const displayId = row.ids.length === 1 ? shortId(row.ids[0]) : `${row.ids.length} objects`;

  return (
    <motion.div
      className="whitelist-entry"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      {/* Icon */}
      <div className="whitelist-entry-icon">
        {row.iconUrl ? (
          <img
            src={row.iconUrl}
            alt=""
            className="wl-token-icon"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling?.style && (e.target.nextSibling.style.display = 'flex'); }}
          />
        ) : null}
        <div
          className="wl-token-icon wl-token-icon--placeholder"
          style={{ display: row.iconUrl ? 'none' : 'flex' }}
        >
          {(row.symbol || row.name || '?')[0]?.toUpperCase()}
        </div>
      </div>

      {/* Info */}
      <div className="whitelist-entry-info">
        {!row.isUnknown ? (
          <>
            <span className="wl-entry-name">
              {row.name}
              {row.isGroup && (
                <span className="wl-group-badge">{row.ids.length} objects</span>
              )}
            </span>
            {row.balance && (
              <span className="wl-entry-balance">{row.balance}</span>
            )}
            <span className="wl-entry-id" title={row.ids[0]}>{displayId}</span>
          </>
        ) : (
          <>
            <span className="wl-entry-name wl-entry-name--unknown">Unknown Object</span>
            <span className="wl-entry-id" title={row.ids[0]}>{shortId(row.ids[0])}</span>
            <span className="wl-entry-note">Not in current wallet objects</span>
          </>
        )}
      </div>

      {/* USD value */}
      {usd && <span className="wl-entry-usd">{usd}</span>}

      {/* Remove button — passes ALL ids for this display row */}
      <button
        className={`remove-button${isRemoving ? ' removing' : ''}`}
        onClick={() => onRemove(row.ids)}
        disabled={isRemoving}
        title="Remove from whitelist — returns to cleanup list"
      >
        {isRemoving ? '...' : 'Remove'}
      </button>
    </motion.div>
  );
}

export default WhitelistPanel;